/**
 * @file 账号安全：修改本人密码（首次登录强制改密走这里）
 * 契约要点：must_change_password=true 时后端只放行 /auth/me、/auth/password、/auth/logout，
 * 其余接口返回 403 AUTH_PASSWORD_CHANGE_REQUIRED，因此改密成功后必须回登录页重新登录。
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthState } from '../auth/context';
import { useToast } from './Toast';
import { describeError, type ErrorText } from './errors';

/**
 * @what 修改密码页
 * @return JSX
 */
export function ChangePasswordPage() {
  const auth = useAuth();
  const state = useAuthState();
  const toast = useToast();
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorText | null>(null);

  const forced = state.identity?.must_change_password ?? false;

  /**
   * @what 提交改密
   * @param event 表单事件
   * @return Promise<void> 无返回值
   * @side 成功后清会话并跳登录页
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError({ title: '本地校验', message: '两次输入的新密码不一致', code: null, traceId: null, details: [] });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await auth.changePassword(oldPassword, newPassword);
      toast.success('密码已修改', '请使用新密码重新登录');
      navigate('/login', { replace: true });
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="lx-page-head">
        <div className="lx-page-head__main">
          <h1 className="lx-page-head__title">账号安全</h1>
          <p className="lx-page-head__sub">
            {forced ? '首次登录必须改掉初始密码，才能用其他功能。' : '改完密码后，三个端都需要重新登录。'}
          </p>
        </div>
        <div className="lx-page-head__aside">
          <span className="lx-badge lx-badge--outline">至少 10 位，大小写 / 数字 / 符号任三类</span>
        </div>
      </header>

      <section className="lx-card" style={{ maxWidth: 460 }}>
        <div className="lx-card__head">
          <h2 className="lx-card__title">修改密码</h2>
          <span className="lx-card__meta">改完立即失效旧会话</span>
        </div>
        <div className="lx-card__body">
          <form className="lx-form" onSubmit={handleSubmit}>
            <div className="lx-field">
              <label className="lx-field__label" htmlFor="lx-old-pwd">
                当前密码
              </label>
              <input
                id="lx-old-pwd"
                className="lx-input"
                type="password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
                required
              />
            </div>
            <div className="lx-field">
              <label className="lx-field__label" htmlFor="lx-new-pwd">
                新密码
              </label>
              <input
                id="lx-new-pwd"
                className="lx-input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                minLength={10}
              />
            </div>
            <div className="lx-field">
              <label className="lx-field__label" htmlFor="lx-confirm-pwd">
                确认新密码
              </label>
              <input
                id="lx-confirm-pwd"
                className="lx-input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={10}
              />
            </div>

            {error ? (
              <div className="lx-auth__error">
                <strong>{error.message}</strong>
                <span className="lx-mono">
                  {error.code ?? 'CLIENT_ERROR'}
                  {error.traceId ? ` · trace ${error.traceId}` : ''}
                </span>
              </div>
            ) : null}

            <button type="submit" className="lx-btn lx-btn--primary lx-btn--block" disabled={busy}>
              {busy ? '提交中…' : '修改密码'}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
